'use client';

import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { fadeUp, staggerContainer } from '@/lib/motion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CTAAction {
  label: string;
  href: string;
  variant?: 'primary' | 'outline' | 'ghost';
}

interface CTABannerProps {
  eyebrow?: string;
  heading: string;
  description?: string;
  primary?: CTAAction;
  secondary?: CTAAction;
  /** Color treatment. */
  variant?: 'navy' | 'cream' | 'ochre';
}

/**
 * Editorial CTA banner — full-bleed section with display headline and dual CTAs.
 */
export function CTABanner({
  eyebrow,
  heading,
  description,
  primary,
  secondary,
  variant = 'navy',
}: CTABannerProps) {
  const tone = {
    navy: {
      bg: 'bg-gradient-to-br from-[#f7f4ef] via-white to-[#f2ede5] text-ink border-y border-border-subtle',
      eyebrow: 'text-accent',
      desc: 'text-ink-secondary',
      primaryBtn: 'primary' as const,
    },
    cream: {
      bg: 'bg-surface-cream text-ink',
      eyebrow: 'text-accent',
      desc: 'text-ink-secondary',
      primaryBtn: 'primary' as const,
    },
    ochre: {
      bg: 'bg-accent text-accent-foreground',
      eyebrow: 'text-accent-foreground/85',
      desc: 'text-accent-foreground/85',
      primaryBtn: 'secondary' as const,
    },
  }[variant];

  return (
    <section className={cn('relative overflow-hidden', tone.bg)}>
      <motion.div
        variants={staggerContainer(0.08, 0)}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '0px 0px -15% 0px' }}
        className="container-page relative py-20 md:py-28 text-center"
      >
        {eyebrow && (
          <motion.span variants={fadeUp} className={cn('eyebrow mb-5 inline-block', tone.eyebrow)}>
            {eyebrow}
          </motion.span>
        )}
        <motion.h2
          variants={fadeUp}
          className="font-display text-display md:text-display-lg leading-[1.05] tracking-tight max-w-3xl mx-auto"
        >
          {heading}
        </motion.h2>
        {description && (
          <motion.p
            variants={fadeUp}
            className={cn('mt-6 text-lead max-w-2xl mx-auto', tone.desc)}
          >
            {description}
          </motion.p>
        )}
        {(primary || secondary) && (
          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            {primary && (
              <Button
                asChild
                variant={primary.variant ?? tone.primaryBtn}
                size="lg"
                className="min-w-[200px]"
              >
                <Link href={primary.href}>
                  {primary.label}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            )}
            {secondary && (
              <Button
                asChild
                variant={
                  secondary.variant ?? (variant === 'navy' ? 'ghost' : 'outline')
                }
                size="lg"
                className={cn(
                  'min-w-[200px]',
                  variant === 'navy' &&
                  !secondary.variant &&
                  'text-primary-foreground hover:bg-surface-light/10',
                )}
              >
                <Link href={secondary.href}>{secondary.label}</Link>
              </Button>
            )}
          </motion.div>
        )}
      </motion.div>
    </section>
  );
}
